import React from 'react';
import { VoiceAndLanguageForm } from './VoiceAndLanguageForm';
import { OverViewCardInfo } from '../OverviewCardInfo';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import './VoiceAndLanguageInfo.scss';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import { User } from '@/types/TAccounts';
import { TUTORIALS } from 'src/enums/ETutorials';
import { safeJSONParse } from '@/utils/helper';

export const VoiceAndLanguageInfo: React.FC<{
	data: User | undefined;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	debrand?: boolean;
}> = ({ data, refetch, debrand }) => {
	const baseClass = 'voice-and-language-info';

	const nonCartesiaSupportedLangs = [
		'Swiss German',
		'Romanian',
		'Vietnamese',
		'Danish',
		'Catalan',
		'Slovak',
		'Ukrainian',
		'Thai',
		'Norwegian',
		'Lithuanian',
		'Latvian',
		'Malay',
		'Indonesian',
		'Hungarian',
		'Greek',
		'Flemish',
		'Finnish',
		'Estonian',
		'Czech',
		'Bulgarian',
		'Arabic',
		'Armenian',
		'Haitian Creole',
	];

	let otherLangVoice: any = {};
	let otherLanguages: string[] = [];
	try {
		const selectedLanguages = data?.selected_languages
			? safeJSONParse(data.selected_languages, [])
			: [];
		otherLanguages = selectedLanguages.filter((item: string) => item !== 'English');
		otherLangVoice = data?.other_language_voice_selected
			? safeJSONParse(data.other_language_voice_selected, {})
			: {};
	} catch (err) {
		console.error('Error parsing other_language_voice_selected:', err);
	}
	const shouldShowHelioMessage =
		otherLanguages.some((lang: string) => nonCartesiaSupportedLangs.includes(lang)) &&
		otherLangVoice?.provider === 'cartesia';

	const formatLanguageList = (languages: string[]) => {
		if (languages.length === 1) {
			return <strong>{languages[0]}</strong>;
		}
		if (languages.length === 2) {
			return (
				<>
					<strong>{languages[0]}</strong> and <strong>{languages[1]}</strong>
				</>
			);
		}

		return (
			<>
				{languages.slice(0, -1).map((lang, index) => (
					<span key={index}>
						<strong>{lang}</strong>,{' '}
					</span>
				))}
				and <strong>{languages[languages.length - 1]}</strong>
			</>
		);
	};

	const parsedSelectedLanguages = safeJSONParse(data?.selected_languages, []);
	const voiceSelected: any = safeJSONParse(data?.voice_selected, {});

	return (
		<OverViewCardInfo
			formActionType="Edit"
			formTitle="Select Languages"
			info={
				<>
					{shouldShowHelioMessage && (
						<div className="flex items-center justify-start py-2 px-6 bg-[#EFF6FF4D] border border-[#DBEAFE80]">
							<span>
								You have selected{' '}
								{formatLanguageList(
									otherLanguages.filter((lang: string) => nonCartesiaSupportedLangs.includes(lang)),
								)}{' '}
								which doesn’t support the selected Cartesia-based voice
								<strong> {voiceSelected?.voiceId || 'your selected voice'} </strong>. So AI will use
								Deepgram's <strong>Helio</strong> voice for communicating in these languages.
								Alternatively, you can select your own Deepgram or 11Labs voice rather than Helio.
							</span>
						</div>
					)}
					<div className={`${baseClass}__item`}>
						<LabelWithDescription label="Languages" />
						<strong>{parsedSelectedLanguages.join(', ')}</strong>
					</div>
				</>
			}
			form={
				<VoiceAndLanguageForm
					refetch={refetch}
					formInitialValues={{
						voice_selected: voiceSelected?.voiceId ?? '',
						selected_languages: parsedSelectedLanguages ?? [],
					}}
					data={data}
				/>
			}
			footer={null}
			modalId={MODAL_IDS.CUSTOMIZE_VOICE_AND_LANGUAGES_MODAL}
			tutorialId={TUTORIALS.NON_ENGLISH_LANGUAGE_SUPPORT}
			displayTutorialActionBtn={true}
			debrand={debrand}
		/>
	);
};
